'use client'

import { cn } from '@/lib/utils'
import { Check } from 'lucide-react'
import { usePathname } from 'next/navigation'

type StepStatus = 'completed' | 'current' | 'upcoming'

interface StepData {
  id: number
  title: React.ReactNode
}

interface StepItemProps {
  step: StepData
  status: StepStatus
}

const StepItem = ({ step, status }: StepItemProps) => {
  const isActive = status === 'completed' || status === 'current'

  return (
    <div className='relative z-10 flex w-[87px] flex-col items-center gap-1'>
      <div
        className={cn(
          'relative flex size-9 items-center justify-center rounded-full font-bold transition-colors duration-200',
          isActive
            ? 'bg-secondary-500 text-white'
            : 'bg-[#e2e2e2] text-[#9f9f9f]',
          // Mũi nhọn bên phải của bước hiện tại
          status === 'current' &&
            "after:absolute after:-right-1 after:top-1/2 after:-translate-y-1/2 after:border-y-[5px] after:border-l-[6px] after:border-y-transparent after:border-l-secondary-500 after:content-['']"
        )}
      >
        {status === 'completed' ? (
          <Check className='size-5' />
        ) : (
          <span>{step.id.toString().padStart(2, '0')}</span>
        )}
      </div>

      <div
        className={cn(
          'text-center text-sm font-medium leading-tight transition-colors duration-200 md:text-base md:leading-tight',
          isActive ? 'text-secondary-500' : 'text-[#9f9f9f]'
        )}
      >
        {step.title}
      </div>
    </div>
  )
}

export default function StepProgressBar() {
  const pathname = usePathname()

  // Giữ nguyên logic step ban đầu (3 bước)
  const getStepFromScreen = (): number => {
    switch (true) {
      case pathname.endsWith('/confirm'):
      case pathname.endsWith('/thankyou'):
        return 3
      case pathname.endsWith('/exchange'):
        return 2
      case pathname.startsWith('/doi-tra/verify'):
        return 1
      default:
        return 1
    }
  }

  const currentStep = getStepFromScreen()

  if (pathname === '/doi-tra') {
    // Do not render the progress bar on the main exchange order page or thank you page
    return null
  }

  const stepsConfig: StepData[] = [
    { id: 1, title: 'Bắt đầu' },
    {
      id: 2,
      title: (
        <>
          Chọn
          <br />
          sản phẩm
        </>
      ),
    },
    { id: 3, title: 'Xác nhận' },
  ]

  const progressRatio = Math.max(
    0,
    Math.min(1, (currentStep - 1) / (stepsConfig.length - 1))
  )

  return (
    <div className='sticky top-0 z-10 bg-neutral-50/90 backdrop-blur-sm'>
      <div className='mx-auto w-full max-w-[720px] px-5 pb-2 pt-3 md:pt-8'>
        <div className='relative flex items-start justify-between'>
          {/* Line nối từ tâm bước đầu đến tâm bước cuối */}
          <div className='absolute left-[43px] right-[43px] top-[16px] h-1 rounded-full bg-[#9f9f9f]/30'>
            <div
              className='h-full rounded-full bg-secondary-500 transition-[width] duration-300 ease-in-out'
              style={{
                width: progressRatio === 0 ? '10px' : `${progressRatio * 100}%`,
              }}
            />
          </div>

          {stepsConfig.map((step) => {
            let status: StepStatus
            if (currentStep > step.id) status = 'completed'
            else if (currentStep === step.id) status = 'current'
            else status = 'upcoming'

            return <StepItem key={step.id} step={step} status={status} />
          })}
        </div>
      </div>
    </div>
  )
}
